import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, Observable } from "rxjs";
import { map } from "rxjs/operators";

import { Role, User } from "app/auth/models";
import { Router } from "@angular/router";

@Injectable({ providedIn: "root" })
export class AuthenticationService {
  //public
  public currentUser: Observable<User>;

  public userCapabilities: Array<string> = [];

  //private
  currentUserSubject: BehaviorSubject<any>;

  /**
   *
   * @param _router
   * @param {HttpClient} _http
   */
  constructor(
    private _router: Router,
    private _http: HttpClient,
  ) {
    this.currentUserSubject = new BehaviorSubject<User>(JSON.parse(localStorage.getItem("currentUser")));
    if (localStorage.getItem("token") && this.currentUserSubject && this.currentUserSubject.value) {
      this.currentUserSubject.value.token = localStorage.getItem("token");

      setInterval(
        async () => {
          const tokenIsValid: any = await this.tokenIsValid();
          if (!tokenIsValid) {
            localStorage.clear();
            await this._router.navigate(["/auth/login"], { queryParams: { returnUrl: window.location.href } });
            return false;
          }
        },
        60 * 60 * 1000,
      );
    }
    this.currentUser = this.currentUserSubject.asObservable();
  }

  // getter: currentUserValue
  public get currentUserValue(): User {
    return this.currentUserSubject.value || null;
  }

  public getCurrentUser() {
    return JSON.parse(localStorage.getItem("currentUser"));
  }

  /**
   *  Confirms if user is admin
   */
  get isAdmin() {
    return this.currentUser && this.currentUserSubject.value?.role && this.currentUserSubject.value?.role === Role.Admin;
  }

  /**
   *  Confirms if user is Employee
   */
  get isEmployee() {
    return this.currentUser && this.currentUserSubject.value?.role && this.currentUserSubject.value.role === Role.Employee;
  }

  roleIs(role: string) {
    return this.currentUser && this.currentUserSubject.value?.role && this.currentUserSubject.value?.role === role;
  }

  tokenIsValid() {
    return this._http
      .post("/auth/validate-token", { token: localStorage.getItem("token") })
      .toPromise()
      .then((r: any) => r.valid)
      .catch((e: any) => {
        console.error(e);
        return false;
      });
  }

  refreshToken() {
    return this._http
      .post("/auth/refresh-token", {})
      .toPromise()
      .then((r: any) => {
        if (r.jwt) {
          localStorage.setItem("currentUser", JSON.stringify(r.user));
          localStorage.setItem("token", r.jwt);
          this.userCapabilities = r.user.capabilities;
          this.currentUserSubject.next(r.user);
        } else {
          this.logout();
        }
      })
      .catch((e: any) => {
        console.error(e);
        return false;
      });
  }

  /**
   * User login
   *
   * @param email
   * @param password
   * @returns user
   */
  login(email: string, password: string) {
    return this._http.post<any>("/auth/login", { email, password }).pipe(
      map((data) => {
        if (data && data.jwt) {
          // store user details and jwt token in local storage to keep user logged in between page refreshes
          localStorage.setItem("currentUser", JSON.stringify(data.user));
          localStorage.setItem("token", data.jwt);
          localStorage.removeItem("config");
          this.userCapabilities = data.user.capabilities;
          this.currentUserSubject.next(data.user);
        }

        return data;
      }),
    );
  }

  /**
   * User logout
   *
   */
  logout() {
    // remove user from local storage to log user out
    localStorage.clear();
    this.userCapabilities = [];
    this.currentUserSubject.next(null);
  }

  hasCapability(name: string) {
    const user: any = this.currentUserValue;
    return user.capabilities.includes(name);
  }

  hasCapabilities(names: string[]) {
    const user: any = this.currentUserValue;
    let found: boolean = false;
    names.map((name: any) => {
      if (!found) {
        found = user.capabilities.includes(name);
      }
    });
    return found;
  }
}
